import { useEffect, useState } from "react";
import { Shield, ShieldCheck, ShieldAlert, AlertTriangle, CheckCircle2, XCircle, Plus, Trash2, Hash, Clock, MapPin, CreditCard, FileSearch, Activity } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

const Card = ({ title, icon: Icon, children, accent = "#ff0033" }) => (
  <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-5" style={{ borderColor: `${accent}22` }}>
    <div className="flex items-center gap-2 mb-3">
      {Icon && <Icon size={16} style={{ color: accent }} />}
      <h3 className="text-xs uppercase tracking-widest text-white/60">{title}</h3>
    </div>
    {children}
  </div>
);

const StatPill = ({ label, value, sub }) => (
  <div className="bg-black/30 border border-white/5 rounded-xl px-4 py-3">
    <div className="text-[10px] uppercase tracking-widest text-white/40">{label}</div>
    <div className="text-2xl font-black text-white mt-1">{value}</div>
    {sub && <div className="text-[10px] text-white/40 mt-0.5">{sub}</div>}
  </div>
);

const TabBtn = ({ active, onClick, id, label, icon: Icon }) => (
  <button
    data-testid={`compliance-tab-${id}`}
    onClick={onClick}
    className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-[10px] sm:text-xs uppercase tracking-widest inline-flex items-center gap-2 transition ${active ? "bg-[#ff0033] text-white" : "bg-white/5 text-white/60 hover:bg-white/10"}`}
  >
    {Icon && <Icon size={13} />} {label}
  </button>
);

export default function Compliance() {
  const [status, setStatus] = useState(null);
  const [settings, setSettings] = useState(null);
  const [queue, setQueue] = useState([]);
  const [blacklist, setBlacklist] = useState([]);
  const [tab, setTab] = useState("dashboard");

  const [zipForm, setZipForm] = useState("");
  const [regForm, setRegForm] = useState("");
  const [hours, setHours] = useState(48);
  const [requireCredit, setRequireCredit] = useState(true);
  const [requireTourist, setRequireTourist] = useState(false);

  const [blForm, setBlForm] = useState({ tax_code: "", document_number: "", risk_level: "fraud", reason: "" });
  const [tcForm, setTcForm] = useState({ legal_country: "Italia", legal_zip: "" });
  const [tcResult, setTcResult] = useState(null);
  const [pmId, setPmId] = useState("");
  const [pmResult, setPmResult] = useState(null);

  // Audit log
  const [audit, setAudit] = useState({ items: [], by_kind: [] });
  const [auditKpi, setAuditKpi] = useState(null);
  const [auditKind, setAuditKind] = useState("");
  const [auditDays, setAuditDays] = useState(30);

  const loadAudit = async () => {
    try {
      const [a, k] = await Promise.all([
        api.get(`/compliance/audit-log?days=${auditDays}${auditKind ? `&kind=${auditKind}` : ""}`),
        api.get(`/compliance/audit-log/kpi?days=${auditDays}`),
      ]);
      setAudit(a.data || { items: [], by_kind: [] });
      setAuditKpi(k.data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { if (tab === "audit") loadAudit(); }, [tab, auditDays, auditKind]);

  const loadAll = async () => {
    try {
      const [s, st, q, b] = await Promise.all([
        api.get("/compliance/status").catch(() => ({ data: null })),
        api.get("/compliance/settings").catch(() => ({ data: null })),
        api.get("/compliance/review-queue").catch(() => ({ data: { items: [] } })),
        api.get("/compliance/blacklist/list").catch(() => ({ data: { items: [] } })),
      ]);
      setStatus(s.data);
      if (st.data) {
        setSettings(st.data);
        setHours(st.data.deferred_release_hours || 48);
        setRequireCredit(!!st.data.require_credit_only);
        setRequireTourist(!!st.data.require_tourist_check);
      }
      setQueue(q.data?.items || []);
      setBlacklist(b.data?.items || []);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const saveSettings = async () => {
    try {
      const payload = {
        blocked_zip_prefixes: zipForm ? zipForm.split(",").map(s => s.trim()).filter(Boolean) : (settings?.blocked_zip_prefixes || []),
        blocked_regions: regForm ? regForm.split(",").map(s => s.trim().toUpperCase()).filter(Boolean) : (settings?.blocked_regions || []),
        deferred_release_hours: +hours,
        require_credit_only: requireCredit,
        require_tourist_check: requireTourist,
      };
      await api.post("/compliance/settings", payload);
      toast.success("Impostazioni compliance aggiornate");
      setZipForm(""); setRegForm("");
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const decideReview = async (id, decision) => {
    const note = decision === "reject" ? window.prompt("Motivo rigetto?") : null;
    try {
      await api.post(`/compliance/review-queue/${id}/decide`, { decision, note });
      toast.success(decision === "approve" ? "Booking approvato" : "Booking rigettato");
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const addBlacklist = async () => {
    if (!blForm.tax_code && !blForm.document_number) {
      toast.error("Almeno uno tra CF e numero documento");
      return;
    }
    try {
      await api.post("/compliance/blacklist/add", blForm);
      toast.success("Soggetto aggiunto alla blacklist globale (hash SHA-256)");
      setBlForm({ tax_code: "", document_number: "", risk_level: "fraud", reason: "" });
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const removeBlacklist = async (id) => {
    if (!window.confirm("Rimuovere il soggetto dalla blacklist globale?")) return;
    try { await api.delete(`/compliance/blacklist/${id}`); toast.success("Rimosso"); loadAll(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const runTouristCheck = async () => {
    try {
      const r = await api.post("/compliance/tourist-check", tcForm);
      setTcResult(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const runCardCheck = async () => {
    if (!pmId) { toast.error("Inserisci payment_method_id Stripe (pm_...)"); return; }
    try {
      const r = await api.post("/compliance/card-funding-check", { payment_method_id: pmId });
      setPmResult(r.data);
    } catch (e) {
      setPmResult({ valid: false, reason: e?.response?.data?.detail });
      toast.error(e?.response?.data?.detail || "Errore");
    }
  };

  return (
    <div className="space-y-6" data-testid="compliance-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-black uppercase tracking-tight flex items-center gap-3">
            Compliance Center <Shield className="text-[#ff0033]" />
          </h1>
          <p className="text-sm text-white/45 mt-1">5 regole enterprise fraud/compliance · architectural mandate</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <TabBtn id="dashboard" label="Dashboard" icon={ShieldCheck} active={tab==="dashboard"} onClick={() => setTab("dashboard")} />
        <TabBtn id="review" label={`Review Queue (${queue.length})`} icon={AlertTriangle} active={tab==="review"} onClick={() => setTab("review")} />
        <TabBtn id="blacklist" label={`Blacklist Globale (${blacklist.length})`} icon={ShieldAlert} active={tab==="blacklist"} onClick={() => setTab("blacklist")} />
        <TabBtn id="checks" label="Test Verifiche" icon={Hash} active={tab==="checks"} onClick={() => setTab("checks")} />
        <TabBtn id="audit" label="Audit Log" icon={FileSearch} active={tab==="audit"} onClick={() => setTab("audit")} />
        <TabBtn id="settings" label="Impostazioni" icon={MapPin} active={tab==="settings"} onClick={() => setTab("settings")} />
      </div>

      {tab === "dashboard" && status && (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Card title="Regola 1 — Tourist-Only Filter" icon={MapPin} accent="#ff0033">
            <StatPill label="Verifiche eseguite" value={status.rules['1_tourist_only_filter']?.checks_logged ?? 0} sub="OCR CAP/Paese verificato" />
          </Card>
          <Card title="Regola 2 — Credit-Only Escrow (PCI-DSS)" icon={CreditCard} accent="#3b82f6">
            <StatPill label="Verifiche Stripe" value={status.rules['2_credit_only_escrow']?.checks_logged ?? 0} sub="card.funding=credit only" />
          </Card>
          <Card title="Regola 3 — 48h Deferred Release" icon={Clock} accent="#facc15">
            <StatPill label="Depositi in hold" value={status.rules['3_deferred_release_48h']?.deferred_active ?? 0} sub={`window ${status.rules['3_deferred_release_48h']?.window_hours ?? 48}h`} />
          </Card>
          <Card title="Regola 4 — Pre-Existing Damages" icon={AlertTriangle} accent="#fb923c">
            <StatPill label="Baseline registrati" value={status.rules['4_pre_existing_damages']?.baseline_records ?? 0} sub="Mappatura zone veicolo" />
          </Card>
          <Card title="Regola 5 — Global Fraud Blacklist" icon={ShieldAlert} accent="#ef4444">
            <StatPill label="Hash anonimizzati" value={status.rules['5_global_fraud_blacklist']?.total_hashed ?? 0} sub="SHA-256 CF/Documento" />
          </Card>
          <Card title="Daemon 48h" icon={Clock} accent="#22c55e">
            <div className="text-xs text-white/60">Scheduler attivo · auto-release depositi appena scaduto window</div>
            <div className="text-xs text-emerald-400 mt-2 inline-flex items-center gap-1"><CheckCircle2 size={12} /> Running</div>
          </Card>
        </div>
      )}

      {tab === "review" && (
        <div className="bg-white/[0.02] border border-white/10 rounded-2xl overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead className="bg-black/40 text-white/60 text-[10px] uppercase tracking-widest">
              <tr>
                <th className="text-left p-3">Cliente</th>
                <th className="text-left p-3">CAP / Paese</th>
                <th className="text-left p-3">Veicolo</th>
                <th className="text-left p-3">Periodo</th>
                <th className="text-left p-3">Motivo</th>
                <th className="text-right p-3">Azione</th>
              </tr>
            </thead>
            <tbody>
              {queue.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-white/40 text-xs">Nessun booking in revisione manuale</td></tr>}
              {queue.map((r) => (
                <tr key={r.id} className="border-t border-white/5" data-testid={`review-row-${r.id}`}>
                  <td className="p-3">
                    <div className="font-medium">{r.customer?.nome} {r.customer?.cognome}</div>
                    <div className="text-[10px] text-white/40">{r.customer?.email}</div>
                  </td>
                  <td className="p-3 text-xs">{r.customer?.cap} · {r.customer?.nazione}</td>
                  <td className="p-3 text-xs">{r.car?.marca} {r.car?.modello}<br/><span className="text-white/40">{r.car?.targa}</span></td>
                  <td className="p-3 text-xs">{r.booking?.data_inizio?.slice(0,10)} → {r.booking?.data_fine?.slice(0,10)}</td>
                  <td className="p-3 text-[11px] text-amber-300">{r.reason}</td>
                  <td className="p-3 text-right space-x-2">
                    <button data-testid={`approve-${r.id}`} onClick={() => decideReview(r.id, "approve")} className="px-3 py-1 rounded-full bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 text-[11px] inline-flex items-center gap-1"><CheckCircle2 size={12}/> Approva</button>
                    <button data-testid={`reject-${r.id}`} onClick={() => decideReview(r.id, "reject")} className="px-3 py-1 rounded-full bg-rose-500/20 border border-rose-500/40 text-rose-300 text-[11px] inline-flex items-center gap-1"><XCircle size={12}/> Rigetta</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "blacklist" && (
        <div className="space-y-4">
          <Card title="Aggiungi soggetto (anonimizzato SHA-256)" icon={Plus} accent="#ef4444">
            <div className="grid md:grid-cols-4 gap-3">
              <input data-testid="bl-cf" placeholder="Codice Fiscale" value={blForm.tax_code} onChange={(e)=>setBlForm({...blForm, tax_code:e.target.value})} className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm" />
              <input data-testid="bl-doc" placeholder="N° documento (CI/Patente)" value={blForm.document_number} onChange={(e)=>setBlForm({...blForm, document_number:e.target.value})} className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm" />
              <select data-testid="bl-risk" value={blForm.risk_level} onChange={(e)=>setBlForm({...blForm, risk_level:e.target.value})} className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm">
                <option value="insolvent">Insolvente</option>
                <option value="theft_attempt">Tentato furto</option>
                <option value="fraud">Frode</option>
                <option value="violence">Violenza</option>
                <option value="other">Altro</option>
              </select>
              <input data-testid="bl-reason" placeholder="Motivo" value={blForm.reason} onChange={(e)=>setBlForm({...blForm, reason:e.target.value})} className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm" />
            </div>
            <button data-testid="bl-add" onClick={addBlacklist} className="mt-3 btn-neon px-5 py-2 rounded-full text-xs inline-flex items-center gap-2"><Plus size={13}/> Aggiungi (hash)</button>
            <div className="text-[10px] text-white/40 mt-2">PII non vengono salvati. Solo SHA-256 cross-tenant.</div>
          </Card>

          <div className="bg-white/[0.02] border border-white/10 rounded-2xl overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
              <thead className="bg-black/40 text-white/60 text-[10px] uppercase tracking-widest">
                <tr>
                  <th className="text-left p-3">CF Hash (12 char)</th>
                  <th className="text-left p-3">Doc Hash (12 char)</th>
                  <th className="text-left p-3">Risk</th>
                  <th className="text-left p-3">Motivo</th>
                  <th className="text-left p-3">Data</th>
                  <th className="text-right p-3"></th>
                </tr>
              </thead>
              <tbody>
                {blacklist.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-white/40 text-xs">Nessuna entry</td></tr>}
                {blacklist.map((b) => (
                  <tr key={b.id} className="border-t border-white/5" data-testid={`bl-row-${b.id}`}>
                    <td className="p-3 font-mono text-[10px]">{b.tax_code_hash?.slice(0,12) || "—"}…</td>
                    <td className="p-3 font-mono text-[10px]">{b.document_number_hash?.slice(0,12) || "—"}…</td>
                    <td className="p-3"><span className="px-2 py-0.5 rounded-full bg-rose-500/20 border border-rose-500/40 text-rose-300 text-[10px] uppercase">{b.risk_level}</span></td>
                    <td className="p-3 text-xs">{b.reason || "—"}</td>
                    <td className="p-3 text-[10px] text-white/40">{b.reported_at?.slice(0,10)}</td>
                    <td className="p-3 text-right"><button data-testid={`bl-remove-${b.id}`} onClick={() => removeBlacklist(b.id)} className="text-rose-300 hover:text-rose-100"><Trash2 size={14} /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "checks" && (
        <div className="grid md:grid-cols-2 gap-4">
          <Card title="Test Tourist-Only Check" icon={MapPin}>
            <div className="space-y-2">
              <input data-testid="tc-country" placeholder="Paese (es. Italia, France)" value={tcForm.legal_country} onChange={(e)=>setTcForm({...tcForm, legal_country:e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm" />
              <input data-testid="tc-zip" placeholder="CAP (es. 40026)" value={tcForm.legal_zip} onChange={(e)=>setTcForm({...tcForm, legal_zip:e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm" />
              <button data-testid="tc-run" onClick={runTouristCheck} className="btn-neon px-4 py-2 rounded-full text-xs">Verifica</button>
              {tcResult && (
                <div className={`mt-3 p-3 rounded-lg text-xs ${tcResult.eligible ? "bg-emerald-500/10 border border-emerald-500/30 text-emerald-200" : "bg-rose-500/10 border border-rose-500/30 text-rose-200"}`}>
                  {tcResult.eligible ? "✓ ELEGGIBILE (turista)" : `✗ ${tcResult.reason}`}
                </div>
              )}
            </div>
          </Card>

          <Card title="Test Stripe Card Funding (Credit-Only)" icon={CreditCard} accent="#3b82f6">
            <div className="space-y-2">
              <input data-testid="pm-id" placeholder="payment_method_id (pm_...)" value={pmId} onChange={(e)=>setPmId(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm font-mono" />
              <button data-testid="pm-run" onClick={runCardCheck} className="btn-neon px-4 py-2 rounded-full text-xs">Verifica funding</button>
              {pmResult && (
                <div className={`mt-3 p-3 rounded-lg text-xs ${pmResult.valid ? "bg-emerald-500/10 border border-emerald-500/30 text-emerald-200" : "bg-rose-500/10 border border-rose-500/30 text-rose-200"}`}>
                  {pmResult.valid ? `✓ CREDIT (${pmResult.brand} *${pmResult.last4})` : `✗ ${pmResult.reason}`}
                </div>
              )}
            </div>
          </Card>
        </div>
      )}

      {tab === "audit" && (
        <div className="space-y-4">
          {auditKpi && (
            <div className="grid md:grid-cols-4 gap-3">
              <StatPill label={`Eventi totali (${auditKpi.window_days}gg)`} value={auditKpi.total_events} sub="Verifiche eseguite" />
              <StatPill label="Frodi bloccate" value={auditKpi.fraud_blocks} sub="Blacklist hits (booking + contract)" />
              <StatPill label="Carte rifiutate" value={auditKpi.card_funding_blocks} sub="Debit/Prepaid bloccate" />
              <StatPill label="Baseline danni" value={auditKpi.baseline_damages_recorded} sub="Mappature pre-existing" />
            </div>
          )}
          <div className="flex flex-wrap gap-2 items-center">
            <select data-testid="audit-kind" value={auditKind} onChange={(e)=>setAuditKind(e.target.value)} className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-xs">
              <option value="">Tutti gli eventi</option>
              <option value="auto_reject_blacklist">Auto-reject Blacklist (Booking)</option>
              <option value="auto_reject_blacklist_contract">Auto-reject Blacklist (Contratto)</option>
              <option value="card_funding_block">Card Funding Block</option>
              <option value="card_funding_check">Card Funding Check (manuale)</option>
              <option value="tourist_check">Tourist Check (manuale)</option>
              <option value="baseline_damage_intake">Baseline Danno Pre-esistente</option>
              <option value="review_approved">Review Approvate</option>
              <option value="review_rejected">Review Rigettate</option>
            </select>
            <select data-testid="audit-days" value={auditDays} onChange={(e)=>setAuditDays(+e.target.value)} className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-xs">
              <option value={7}>Ultimi 7 giorni</option>
              <option value={30}>Ultimi 30 giorni</option>
              <option value={90}>Ultimi 90 giorni</option>
              <option value={365}>Ultimo anno</option>
            </select>
            <button data-testid="audit-refresh" onClick={loadAudit} className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-xs inline-flex items-center gap-1 hover:bg-white/10"><Activity size={12} /> Aggiorna</button>
            <a
              data-testid="audit-export-csv"
              href={`${process.env.REACT_APP_BACKEND_URL}/api/ops/audit-export.csv?days=${auditDays}${auditKind ? `&kind=${auditKind}` : ""}`}
              download
              onClick={(e) => {
                // attach Authorization via fetch+blob fallback
                e.preventDefault();
                const url = `${process.env.REACT_APP_BACKEND_URL}/api/ops/audit-export.csv?days=${auditDays}${auditKind ? `&kind=${auditKind}` : ""}`;
                const token = localStorage.getItem("token");
                fetch(url, { headers: { Authorization: `Bearer ${token}` } })
                  .then(r => r.blob())
                  .then(blob => {
                    const u = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = u; a.download = `compliance_audit_${Date.now()}.csv`;
                    a.click();
                    setTimeout(() => URL.revokeObjectURL(u), 1000);
                  });
              }}
              className="px-3 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-xs inline-flex items-center gap-1 hover:bg-emerald-500/20"
            >
              📥 Esporta CSV
            </a>
            <a
              data-testid="audit-export-pdf"
              href="#"
              onClick={(e) => {
                e.preventDefault();
                const url = `${process.env.REACT_APP_BACKEND_URL}/api/compliance/report.pdf?days=${auditDays}`;
                const token = localStorage.getItem("token");
                toast.info("Generazione PDF in corso...");
                fetch(url, { headers: { Authorization: `Bearer ${token}` } })
                  .then(r => r.blob())
                  .then(blob => {
                    const u = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = u; a.download = `compliance_report_${Date.now()}.pdf`;
                    a.click();
                    toast.success("Report PDF scaricato");
                    setTimeout(() => URL.revokeObjectURL(u), 1000);
                  });
              }}
              className="px-3 py-2 rounded-lg bg-rose-500/10 border border-rose-500/30 text-rose-300 text-xs inline-flex items-center gap-1 hover:bg-rose-500/20"
            >
              📄 Report PDF
            </a>
            <button
              data-testid="verify-chain"
              onClick={async () => {
                try {
                  const r = await api.get("/compliance/audit-chain/verify");
                  if (r.data.ok) {
                    toast.success(`✓ Chain integra · ${r.data.total_events} eventi verificati con SHA-256`);
                  } else {
                    toast.error(`⚠ ${r.data.tampered_events.length} eventi alterati!`);
                  }
                } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
              }}
              className="px-3 py-2 rounded-lg bg-blue-500/10 border border-blue-500/30 text-blue-300 text-xs inline-flex items-center gap-1 hover:bg-blue-500/20"
            >
              🔐 Verifica Chain
            </button>
          </div>
          <div className="bg-white/[0.02] border border-white/10 rounded-2xl overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
              <thead className="bg-black/40 text-white/60 text-[10px] uppercase tracking-widest">
                <tr>
                  <th className="text-left p-3">Quando</th>
                  <th className="text-left p-3">Tipo evento</th>
                  <th className="text-left p-3">Cliente</th>
                  <th className="text-left p-3">Contratto / Booking</th>
                  <th className="text-left p-3">Dettagli</th>
                </tr>
              </thead>
              <tbody>
                {(audit.items || []).length === 0 && <tr><td colSpan={5} className="p-6 text-center text-white/40 text-xs">Nessun evento nel periodo selezionato</td></tr>}
                {(audit.items || []).map((it) => {
                  const kindMap = {
                    auto_reject_blacklist: { color: "rose", label: "🚫 Blacklist Booking" },
                    auto_reject_blacklist_contract: { color: "rose", label: "🚫 Blacklist Contratto" },
                    card_funding_block: { color: "amber", label: "💳 Carta Bloccata" },
                    card_funding_check: { color: "blue", label: "💳 Verifica Carta" },
                    tourist_check: { color: "purple", label: "🌍 Tourist Check" },
                    baseline_damage_intake: { color: "orange", label: "🔧 Danno Baseline" },
                    review_approved: { color: "emerald", label: "✓ Review Approvata" },
                    review_rejected: { color: "rose", label: "✗ Review Rigettata" },
                  };
                  const m = kindMap[it.kind] || { color: "slate", label: it.kind };
                  const colorMap = {
                    rose: "bg-rose-500/15 border-rose-500/40 text-rose-300",
                    amber: "bg-amber-500/15 border-amber-500/40 text-amber-300",
                    blue: "bg-blue-500/15 border-blue-500/40 text-blue-300",
                    purple: "bg-purple-500/15 border-purple-500/40 text-purple-300",
                    orange: "bg-orange-500/15 border-orange-500/40 text-orange-300",
                    emerald: "bg-emerald-500/15 border-emerald-500/40 text-emerald-300",
                    slate: "bg-slate-500/15 border-slate-500/40 text-slate-300",
                  };
                  return (
                    <tr key={it.id || (it.checked_at+it.kind)} className="border-t border-white/5" data-testid={`audit-row-${it.kind}`}>
                      <td className="p-3 text-[11px] text-white/60 whitespace-nowrap">{it.checked_at?.replace('T', ' ').slice(0, 19)}</td>
                      <td className="p-3"><span className={`px-2 py-0.5 rounded-full border text-[10px] uppercase tracking-wider ${colorMap[m.color]}`}>{m.label}</span></td>
                      <td className="p-3 text-xs">{it.customer_label || it.customer_id || "—"}</td>
                      <td className="p-3 text-xs">{it.contract_label || it.contract_id || it.booking_id || "—"}</td>
                      <td className="p-3 text-[11px] text-white/60">
                        {it.risk_level && <div>Rischio: <span className="text-rose-300 font-mono">{it.risk_level}</span></div>}
                        {it.funding && <div>Funding: <span className="text-amber-300 font-mono">{it.funding}</span> {it.brand && `(${it.brand} *${it.last4 || ""})`}</div>}
                        {it.decided_by && <div>Operatore: <span className="text-emerald-300">{it.decided_by}</span></div>}
                        {it.note && <div className="text-white/40">Note: {it.note}</div>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "settings" && settings && (
        <Card title="Configurazione Compliance" icon={MapPin}>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-white/50">CAP vietati (prefissi separati da virgola)</label>
              <input data-testid="set-zips" placeholder={settings.blocked_zip_prefixes?.join(", ") || "es. 80, 40026"} value={zipForm} onChange={(e)=>setZipForm(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm mt-1" />
              <div className="text-[10px] text-white/40 mt-1">Attuale: {(settings.blocked_zip_prefixes || []).join(", ") || "—"}</div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-white/50">Regioni vietate (separati da virgola)</label>
              <input data-testid="set-regs" placeholder={settings.blocked_regions?.join(", ") || "es. CAMPANIA, EMILIA_ROMAGNA"} value={regForm} onChange={(e)=>setRegForm(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm mt-1" />
              <div className="text-[10px] text-white/40 mt-1">Attuale: {(settings.blocked_regions || []).join(", ") || "—"}</div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-white/50">Deferred Release (ore)</label>
              <input data-testid="set-hours" type="number" value={hours} onChange={(e)=>setHours(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm mt-1" />
            </div>
            <div className="space-y-2 flex flex-col justify-center">
              <label className="text-xs text-white/70 inline-flex items-center gap-2 cursor-pointer">
                <input data-testid="set-credit" type="checkbox" checked={requireCredit} onChange={(e)=>setRequireCredit(e.target.checked)} /> Richiedi solo carte CREDIT per cauzione
              </label>
              <label className="text-xs text-white/70 inline-flex items-center gap-2 cursor-pointer">
                <input data-testid="set-tourist" type="checkbox" checked={requireTourist} onChange={(e)=>setRequireTourist(e.target.checked)} /> Abilita Tourist-Only filter al booking
              </label>
            </div>
          </div>
          <button data-testid="set-save" onClick={saveSettings} className="mt-4 btn-neon px-5 py-2 rounded-full text-xs">Salva impostazioni</button>
        </Card>
      )}
    </div>
  );
}
